import { Integration, IntegrationProvider, IntegrationThirdParty } from "./Integrations";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import CheckMarkIcon from "@icons/check-verified-02.svg?react";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useEffect, useRef, useState } from "react";
import LabeledSwitch from "@/common/components/LabeledSwitch";
import Divider from "@/common/components/Divider";
import { parseHexColor } from "@/common/utils/hexColor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { doctorMappingQueryKey, fetchDoctorMapping } from "@common/lib/api/bridge";
import { checkedInSyncQueryKey, fetchCheckedInSync, putCheckedInSync } from "@common/lib/api/integrationCheckedInSync";
import DoctorMappingModal from "./DoctorMappingModal";
import AvimarkHub from "@/features/avimark-export/hub/AvimarkHub";
import { useIsHappyBridgeAvimark } from "@/features/avimark-export/hooks/useIsHappyBridgeAvimark";
import CornerstoneAutoFinalizeRow from "./CornerstoneAutoFinalizeRow";
import { useCornerstoneAutoFinalize } from "./useCornerstoneAutoFinalize";

/**
 * What each connected integration does for the clinic (EN-1026). Every provider pulls appointments;
 * DaySmart and CSV import have no writeback path, so they do not write documents, and neither does a
 * provider whose export toggle is off. A function, not a
 * module-level Set: this file and Integrations.tsx import each other, so the enums are not yet
 * initialised when this module first evaluates.
 */
const writesDocuments = (provider: Integration["provider"]) =>
    [
        IntegrationThirdParty.Cornerstone,
        IntegrationThirdParty.AVImark,
        IntegrationThirdParty.ImproMed,
        IntegrationProvider.VetSpire,
        IntegrationProvider.EzyVet,
        IntegrationProvider.Shepherd,
        IntegrationProvider.Pulse,
    ].includes(provider);

/** Masks a credential down to its last four characters; a key that short is masked entirely. */
const maskSecret = (secret: string) => `••••••••${secret.length > 8 ? secret.slice(-4) : ""}`;

const ConnectedIntegration = ({ integration }: { integration: Integration }) => {
    // hooks
    const { accountData, pmsIntegrationData, updateIntegrationExportEnabled, updateIntegrationDefaultAlertColor } =
        useAccountContext();

    // constants
    const connectedAccountIntegration = pmsIntegrationData?.find(
        (pmsInt) => pmsInt.id === integration.account_integration_id,
    );
    const providerExportEnabled = connectedAccountIntegration?.export_enabled;
    const canToggleExport =
        integration.provider === IntegrationThirdParty.AVImark ||
        integration.provider === IntegrationThirdParty.ImproMed ||
        integration.provider === IntegrationThirdParty.Cornerstone;

    // EN-997: a HappyBridge account can still carry Bitwerx-era doctor rows. There is no
    // HappyBridge card: bridge accounts render on the PMS card (matched via connected_third_party),
    // which may be bound to the active Bitwerx row while the cutover is pending. So look for a
    // happybridge row anywhere in the account's integrations, active or not.
    const isHappyBridge =
        canToggleExport &&
        !!pmsIntegrationData?.some(
            (pmsInt) => pmsInt.integrations?.integration_name === IntegrationProvider.HappyBridge,
        );
    // EN-1249: the hub renders only once the settings probe confirms HappyBridge AVImark. On "error" the card
    // stays as it is, with a retry, and never falls back to ExportSettingsModal (EN-1248 rule).
    const avimarkProbeEnabled = isHappyBridge && integration.provider === IntegrationThirdParty.AVImark;
    const avimarkProbe = useIsHappyBridgeAvimark(avimarkProbeEnabled);
    // EN-1320: the auto-finalize row shows only when the setting loads (a 404 resolves null: not a bridge Cornerstone clinic).
    const cornerstoneAutoFinalizeEnabled = isHappyBridge && integration.provider === IntegrationThirdParty.Cornerstone;
    const autoFinalize = useCornerstoneAutoFinalize(cornerstoneAutoFinalizeEnabled);
    const accountId = accountData?.id;
    const doctorMapping = useQuery({
        queryKey: doctorMappingQueryKey(accountId),
        queryFn: ({ queryKey }) => fetchDoctorMapping(queryKey[1] as string),
        enabled: isHappyBridge && !!accountId,
    });
    const queryClient = useQueryClient();
    const showCheckedInSync = isHappyBridge && integration.provider === IntegrationThirdParty.Cornerstone;
    const checkedInSync = useQuery({
        queryKey: checkedInSyncQueryKey(accountId),
        queryFn: fetchCheckedInSync,
        enabled: showCheckedInSync && !!accountId,
    });
    const checkedInSyncInFlight = useRef(false);
    const checkedInSyncMutation = useMutation({
        onSettled: () => {
            checkedInSyncInFlight.current = false;
        },
        mutationFn: (enabled: boolean) => putCheckedInSync(enabled),
        onMutate: async (enabled: boolean) => {
            const key = checkedInSyncQueryKey(accountId);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData<boolean>(key);
            queryClient.setQueryData(key, enabled);
            // The key travels with the mutation so a clinic switch mid-save cannot write this
            // clinic's result into the next clinic's cache.
            return { previous, key };
        },
        onSuccess: (enabled, _enabled, context) => {
            queryClient.setQueryData(context.key, enabled);
        },
        onError: (error, _enabled, context) => {
            if (context && context.previous !== undefined) {
                queryClient.setQueryData(context.key, context.previous);
            } else if (context) {
                // Defensive: the switch is disabled until the value loads, so previous is always set.
                void queryClient.invalidateQueries({ queryKey: context.key });
            }
            // putCheckedInSync answers 409 when there is no active Cornerstone happy-bridge installation.
            const status = (error as { response?: { status?: number } })?.response?.status;
            toast.error(
                status === 409 ? "Cornerstone isn't connected through HappyBridge yet." : "Couldn't save. Try again.",
            );
        },
    });
    const checkedInSyncDisabled =
        checkedInSync.data === undefined || checkedInSync.isError || checkedInSyncMutation.isPending;
    const canReconcileDoctors = isHappyBridge && (doctorMapping.data?.oldDoctors.length ?? 0) > 0;

    // component state
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
    const [exportEnabledToggle, setExportEnabledToggle] = useState<boolean>(providerExportEnabled || false);
    const [defaultAlertColor, setDefaultAlertColor] = useState<string | null>(
        connectedAccountIntegration?.cornerstone_default_alert_color ?? null,
    );
    const [defaultAlertColorInput, setDefaultAlertColorInput] = useState<string>(defaultAlertColor ?? "");
    const [defaultAlertColorError, setDefaultAlertColorError] = useState<string | null>(null);
    // The last color known to be persisted (server-confirmed, or synced from provider data) --
    // distinct from `defaultAlertColor`, which the picker's onChange optimistically overwrites
    // before any save happens. `saveDefaultAlertColor` reads this (not `defaultAlertColor`) to
    // compute a correct rollback target, since by the time it runs for the picker path,
    // `defaultAlertColor` may already hold the new, not-yet-saved value.
    const committedAlertColorRef = useRef<string | null>(defaultAlertColor);
    // Whether the picker's value has actually changed since the last save -- a bare focus/blur
    // with no interaction (or a cancelled picker dialog) must not persist the swatch's `#FFFFFF`
    // placeholder as a real, explicit default. Also read by the provider-sync effect below, so an
    // external cache update (from an unrelated completing save) can't stomp the swatch's live,
    // uncommitted value while the user is still mid-drag.
    const pickerDirtyRef = useRef(false);
    // Same idea for the text field: true from the moment the user starts typing until its own
    // blur has processed the result, so the provider-sync effect doesn't overwrite a draft the
    // user is still editing.
    const textInputDirtyRef = useRef(false);
    // The picker's blur and the text field's blur can both call saveDefaultAlertColor
    // independently (e.g. the user edits one, tabs to the other before the first request
    // resolves). A client-side "ignore the older response" check isn't enough on its own:
    // `updateIntegrationDefaultAlertColor` writes the shared React Query cache unconditionally
    // on success, and this component's own provider-sync effect (below) copies that cache value
    // straight into local state and `committedAlertColorRef` with no notion of which save
    // started later -- so an older, slower request can still overwrite a newer one's result both
    // in the DB and in local state. Chaining every save onto this ref serializes the actual
    // network calls so they always reach the server in dispatch order, which is the only way to
    // guarantee the last-dispatched save is also the last one applied everywhere. Chained with
    // `.catch` internally so a rejected save can never leave the queue itself rejected -- once
    // that happened, every later `.then()` chained onto it would skip straight past its own
    // callback (rejection just propagates), silently dropping every subsequent save for the rest
    // of the session even though the UI keeps looking like it's saving.
    const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
    // Ordinal token of the most recently DISPATCHED save (not the color value -- two different
    // dispatches can share the same color, e.g. a retry, and must still be told apart). A queued
    // save whose request eventually fails checks this before reverting the display or showing
    // its error, so a stale failure can't undo or misreport a save that was dispatched after it,
    // even one targeting the identical hex.
    const latestSaveTokenRef = useRef(0);

    /**
     * Toggle export enabled
     */
    const handleExportEnabledToggle = async () => {
        if (!integration.account_integration_id) {
            return;
        }

        const newExportEnabled = !exportEnabledToggle;

        // update the db && app state
        await updateIntegrationExportEnabled(integration.account_integration_id, newExportEnabled);
    };

    /**
     * Save the account-level default record color. The optimistic UI update happens immediately
     * (so the swatch/text field reflect the user's latest pick right away); the actual network
     * call is queued onto `saveQueueRef` so overlapping saves from the picker and the text field
     * always reach the server -- and therefore the shared cache -- in the order they were
     * dispatched, never out of order.
     */
    const saveDefaultAlertColor = (color: string | null) => {
        if (!integration.account_integration_id) {
            return;
        }
        const accountIntegrationId = integration.account_integration_id;

        const token = ++latestSaveTokenRef.current;
        setDefaultAlertColor(color);
        setDefaultAlertColorError(null);

        saveQueueRef.current = saveQueueRef.current.then(async () => {
            const previousColor = committedAlertColorRef.current;
            let success = false;
            try {
                success = await updateIntegrationDefaultAlertColor(accountIntegrationId, color);
            } catch (error) {
                // Caught here (not left to propagate) so this step's promise always resolves --
                // an uncaught throw would leave `saveQueueRef` itself rejected, and every save
                // chained onto it afterwards would then skip its own callback entirely (a
                // rejected promise's `.then()` callback never runs), silently dropping every
                // later save for the rest of the session while the UI keeps looking like it saved.
                Sentry.captureException(error);
            }

            if (success) {
                committedAlertColorRef.current = color;
                return;
            }

            // Only revert the display / surface the error if no newer save has been dispatched
            // since -- checked by ordinal token, not by color value, since a later save can
            // legitimately target the same hex (e.g. a retry) and must still win over this
            // now-superseded failure.
            if (latestSaveTokenRef.current === token) {
                setDefaultAlertColor(previousColor);
                setDefaultAlertColorError("Unable to save default record color");
            }
        });
    };

    /**
     * The native color-picker fires `onChange` continuously while the user drags inside the
     * picker dialog, so only update local optimistic state here — the actual persist is deferred
     * to `onBlur` (picker close) to avoid one Supabase UPDATE per drag frame.
     */
    const handleDefaultAlertColorPickerChange = (color: string) => {
        pickerDirtyRef.current = true;
        setDefaultAlertColor(color);
    };

    /**
     * Persist the color-picker value once the user is done dragging (picker closes / input blurs).
     * Bails out entirely if the picker never actually changed since the last save (a bare
     * focus/blur, or a cancelled picker dialog): the input's DOM value always shows a real
     * 6-digit hex — the `#FFFFFF` placeholder when no default is set — and saving it
     * unconditionally would silently turn an unset default into an explicit white the moment
     * the user merely tabs through the control. When it IS dirty, reads the input's own current
     * value rather than the `defaultAlertColor` state: some browsers fire the picker's final
     * `change` and `blur` together, and React may not have flushed the `change` handler's state
     * update before this handler's closure was created, so `defaultAlertColor` here could still
     * be the previous value. `e.target.value` is the DOM's actual current value and isn't
     * subject to that race. The native picker only ever emits a valid 6-digit hex, so no
     * validation is needed here.
     */
    const handleDefaultAlertColorPickerBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!pickerDirtyRef.current) {
            return;
        }
        pickerDirtyRef.current = false;
        saveDefaultAlertColor(e.target.value);
    };

    /**
     * Validate the hex text input on blur; only save when it's empty (clears the default) or a
     * valid 6-digit hex, so a mid-typed value is never persisted or silently discarded. Skips
     * the save entirely if the resolved value is unchanged from what's currently shown -- a bare
     * focus/blur with no real edit (e.g. tabbing through the form) would otherwise issue a
     * redundant write every time, mirroring the picker's own dirty check above.
     */
    const handleDefaultAlertColorInputBlur = () => {
        textInputDirtyRef.current = false;

        const parsed = parseHexColor(defaultAlertColorInput);

        if (parsed.status === "invalid") {
            setDefaultAlertColorError("Enter a 6-digit hex color (e.g. #800080), or leave blank to clear it");
            return;
        }

        setDefaultAlertColorError(null);

        const resolved = parsed.status === "valid" ? parsed.value : null;
        if (resolved === defaultAlertColor) {
            return;
        }

        saveDefaultAlertColor(resolved);
    };

    // keep component state in sync with provider data
    useEffect(() => {
        setExportEnabledToggle(providerExportEnabled || false);
    }, [providerExportEnabled]);

    useEffect(() => {
        const providerColor = connectedAccountIntegration?.cornerstone_default_alert_color ?? null;
        // Always track the true server-confirmed value for rollback bookkeeping, regardless of
        // what's currently on screen.
        committedAlertColorRef.current = providerColor;

        // But don't stomp the visible value while the user has an in-progress, not-yet-committed
        // edit: this effect fires whenever the shared cache changes, which happens on ANY
        // completing save (e.g. one dispatched from a much earlier interaction) -- without this
        // guard, that unrelated update could overwrite the picker mid-drag or the text field
        // mid-typing with an older value, and the next blur would then persist that stale value
        // instead of what the user is actually looking at.
        if (!pickerDirtyRef.current && !textInputDirtyRef.current) {
            setDefaultAlertColor(providerColor);
        }
    }, [connectedAccountIntegration?.cornerstone_default_alert_color]);

    // keep the hex text input in sync with the committed value (external updates, and a revert after a failed save)
    useEffect(() => {
        setDefaultAlertColorInput(defaultAlertColor ?? "");
    }, [defaultAlertColor]);

    return (
        <div className="w-full h-full border border-gray-200 rounded-xl flex flex-col gap-6 p-6 my-4 items-between">
            <div className="flex justify-between items-center flex-wrap md:flex-nowrap gap-5">
                <div className="flex items-center gap-x-4 bg-white">
                    <img
                        src={integration.imageUrl}
                        alt={integration.name}
                        className={classNames(
                            integration?.disabled && "opacity-50",
                            "h-12 w-12 flex-none rounded-lg bg-white object-cover ring-1 ring-gray-300",
                        )}
                    />
                    <div
                        className={classNames(
                            integration?.disabled ? "text-gray-400" : "text-gray-900",
                            "text-md font-semibold leading-6",
                        )}
                    >
                        {integration.name}
                        <div className="flex flex-wrap gap-2 mt-1">
                            <span className="rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                                Pulls appointments
                            </span>
                            {writesDocuments(integration.provider) && (!canToggleExport || exportEnabledToggle) && (
                                <span className="rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                                    Writes documents
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex px-3 py-2 justify-center items-center bg-white gap-2 border border-max-300 shadow-sm rounded-md text-max-900">
                    <CheckMarkIcon className="h-5 w-5" aria-hidden={true} />
                    <span className="text-sm text-max-900 font-semibold">Connected</span>
                </div>
            </div>
            {integration?.lastRequest && (
                <div className="flex items-center justify-between bg-white">
                    <span className="text-sm font-normal leading-6 text-gray-600">Last Request</span>
                    <div className="text-sm font-normal leading-6 text-gray-600">
                        {integration?.lastRequest ? new Date(integration.lastRequest).toLocaleString() : "-"}
                    </div>
                </div>
            )}

            {/* Enable export toggle */}
            {canToggleExport && (
                <LabeledSwitch
                    label="Enable Export"
                    checked={exportEnabledToggle}
                    onChange={handleExportEnabledToggle}
                />
            )}
            {showCheckedInSync && (
                <div className="flex flex-col gap-1">
                    <LabeledSwitch
                        label="Sync checked-in patients"
                        checked={checkedInSync.data ?? false}
                        onChange={(checked) => {
                            // A ref, not isPending: two events in one batch share the same render's closure.
                            if (checkedInSyncMutation.isPending || checkedInSyncInFlight.current) return;
                            checkedInSyncInFlight.current = true;
                            checkedInSyncMutation.mutate(checked);
                        }}
                        disabled={checkedInSyncDisabled}
                    />
                    <p className="text-sm text-gray-600">
                        Patients checked in on the Daily Planner appear as visits in a "Checked-In" room.
                    </p>
                    {checkedInSync.isError && (
                        <p className="text-sm text-gray-700">
                            Couldn't load this setting. Refresh the page to try again.
                        </p>
                    )}
                </div>
            )}
            {avimarkProbeEnabled && avimarkProbe.status === "yes" && <AvimarkHub />}
            {avimarkProbeEnabled && avimarkProbe.status === "error" && (
                <p className="text-sm text-gray-700">
                    Couldn't load AVImark settings.{" "}
                    <button type="button" className="underline" onClick={() => avimarkProbe.refetch()}>
                        Try again
                    </button>
                </p>
            )}

            {canReconcileDoctors && (
                <div className="flex flex-row-reverse bg-white">
                    <button type="button" className="underline text-max-900" onClick={() => setReconcileOpen(true)}>
                        Reconcile Cornerstone doctors
                    </button>
                </div>
            )}
            {reconcileOpen && <DoctorMappingModal accountId={accountId} open onClose={() => setReconcileOpen(false)} />}

            {integration?.provider === IntegrationThirdParty.Cornerstone ? (
                <>
                    <Divider />
                    <div className="flex items-center justify-between bg-white flex-wrap gap-2">
                        <span className="text-sm font-normal leading-6 text-gray-600">Default Record Color</span>
                        <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={defaultAlertColor ?? "#FFFFFF"}
                                    onChange={(e) => handleDefaultAlertColorPickerChange(e.target.value)}
                                    onBlur={handleDefaultAlertColorPickerBlur}
                                    aria-label="Default record color"
                                />
                                <input
                                    type="text"
                                    value={defaultAlertColorInput}
                                    onChange={(e) => {
                                        textInputDirtyRef.current = true;
                                        setDefaultAlertColorInput(e.target.value);
                                    }}
                                    onBlur={handleDefaultAlertColorInputBlur}
                                    className="border rounded px-2 py-1 text-sm w-24"
                                    placeholder="#RRGGBB (none)"
                                />
                            </div>
                            {defaultAlertColorError && (
                                <span className="text-red-500 text-xs">{defaultAlertColorError}</span>
                            )}
                        </div>
                    </div>
                    {autoFinalize.data && (
                        <>
                            <Divider />
                            <CornerstoneAutoFinalizeRow key={accountId} />
                        </>
                    )}
                    <Divider />
                    <div className="flex items-center justify-between bg-white flex-wrap">
                        <span className="text-sm font-normal leading-6 text-gray-600">Account ID</span>
                        <div className="text-sm font-normal leading-6 text-max-900">{accountData?.id}</div>
                    </div>
                    <div className="flex items-center justify-between bg-white flex-wrap">
                        <span className="text-sm font-normal leading-6 text-gray-600">API Key</span>
                        {accountData?.api_key && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-normal leading-6 text-max-900 break-all">
                                    {apiKeyRevealed ? accountData.api_key : maskSecret(accountData.api_key)}
                                </span>
                                <button
                                    type="button"
                                    className="underline text-sm text-max-900"
                                    aria-label={apiKeyRevealed ? "Hide API key" : "Show API key"}
                                    onClick={() => setApiKeyRevealed((revealed) => !revealed)}
                                >
                                    {apiKeyRevealed ? "Hide" : "Show"}
                                </button>
                            </div>
                        )}
                    </div>
                </>
            ) : integration?.provider === IntegrationProvider.VetSpire ||
              integration?.provider === IntegrationProvider.Pulse ? (
                <>
                    <Divider />
                    <div className="flex items-center justify-between bg-white flex-wrap gap-2">
                        <span className="text-sm font-normal leading-6 text-gray-600 shrink-0">API URL</span>
                        <div className="text-sm font-normal leading-6 text-max-900 text-right break-all min-w-0">
                            {integration.apiUrl || "-"}
                        </div>
                    </div>
                    {integration?.practiceId ? (
                        <div className="flex items-center justify-between bg-white flex-wrap">
                            <span className="text-sm font-normal leading-6 text-gray-600">Practice ID</span>
                            <div className="text-sm font-normal leading-6 text-max-900">{integration.practiceId}</div>
                        </div>
                    ) : null}
                </>
            ) : (
                integration?.practiceId && (
                    <>
                        <Divider />
                        <div className="flex items-center justify-between bg-white flex-wrap">
                            <span className="text-sm font-normal leading-6 text-gray-600">Practice ID</span>
                            <div className="text-sm font-normal leading-6 text-max-900">{integration.practiceId}</div>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default ConnectedIntegration;
